import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { ALIGN_ITEMS, FLEX_CONTAINER_COLUMN, FLEX_CONTAINER_ROW, H_100, JUSTIFY_CONTENT, TEXT_TRUNCATE } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { createHtmlElement, empty } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { AttachmentDTO } from "@cervval.js/common-attachments/src/attachment.model";
import { anyReallyAny, ensureLoaded } from "@cervval.js/common/src/tools/object.tools";
import { ButtonSize, ButtonStyle, CjsButton } from "../../components/button/button";
import { showErrorInDialog } from "../../components/dialog/dialog";
import { CjsFilePicker, FilePickerOptions } from "../../pickers/filePicker/filePicker";
import { CjsEditor, EditorOptions, EditorSetValueOptions, defaultEditorOptions } from "../editor";
import { I18nType, i18n } from "./i18n/i18n";

ensureLoaded(CjsFilePicker);

/** Used in this.createListWithHref() */
export interface FileWithLink {

    file: File;

    /** Link used for href    */
    linkToDownload?: string;

    /** Link used for href    */
    attachment?: AttachmentDTO;
}

/** Options for FileListEditor */
export interface FileListEditorOptions extends EditorOptions {

    /** If true, user can add several times the same file */
    enableSameNameFile: boolean;

    /** on new file added */
    onNewFileAdded?: (file: FileWithLink) => Promise<FileWithLink>;

    /** On file deleted */
    onFileDeleted?: (file: FileWithLink) => Promise<void>;

}

/** Options for FileListEditor */
export const defaultFileListEditorOptions: FileListEditorOptions = {

    /** If true, user can add several times the same file */
    enableSameNameFile: false,

    ...defaultEditorOptions
};

@CustomElement({
    name: "cjs-file-list-editor",
    style: require("./fileListEditor.scss"),
    template: require("./fileListEditor.html"),
    elementCssClasses: ["cjs-editor", FLEX_CONTAINER_COLUMN, H_100],
    i18n
})
export class CjsFileListEditor extends CjsEditor<FileWithLink[], I18nType> {

    /** components */
    @TypedHtmlElements()
    public readonly components!: {
        fileListPicker: CjsFilePicker,
        fileList: HTMLUListElement
    };

    @TypedWebComponentOptions(defaultFileListEditorOptions)
    public readonly options!: WebComponentOptions<FileListEditorOptions>;

    /** Access to options of filePicker */
    public get filePickerOptions(): WebComponentOptions<FilePickerOptions> {
        return this.filePicker.options;
    }

    /** File picker of editor */
    public get filePicker(): CjsFilePicker {
        return this.components.fileListPicker;
    }

    /** List of files witch can be saved. Could not contain several File with the same name */
    protected _getValueArray(): FileWithLink[] {
        return this._value ?? [];
    }

    constructor(options?: Partial<FileListEditorOptions>, filePickerOptions?: Partial<FilePickerOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
        this.filePickerOptions.setWithoutRefresh(filePickerOptions);
    }

    /**
     * Initialize components, input components and callbacks after the element is added to the dom and _components is ready
     */
    public override init(): void {
        super.init();
        this.filePicker.valueChangeEvent.on(() => this._onInputValueChange());
    }

    /** Refresh editor */
    public override refresh(): void {
        super.refresh();
    }

    /**
     * Update the input value from stored value
     */
    protected _refreshInputValue(): void {
        empty(this.components.fileList);
        for (const fileWithLink of this._getValueArray()) {
            this._createListItemForFile(fileWithLink);
        }
    }

    /**
     * Empty file list editor component
     */
    public empty(): void {
        empty(this.components.fileList);
        this._value = [];
        this.filePicker.emptyFiles();
        this.filePicker.refresh();
    }


    /**
     * Set editor value 
     * @param value 
     * @param options 
     * @returns true if value changed
     */
    public override setValue(value: FileWithLink[] | undefined, options?: EditorSetValueOptions): boolean {

        if (this._value !== value) {
            this._value = value;
            if (options?.refreshIfChanged) {
                this.refresh();
            }
            if (options?.dispatchEvents) {
                this._dispatchValueChangedEvent();
            }
            this._refreshInputValue();
            return true;
        }

        return false;
    }

    /**
     * Gets the value corresponding to the current input value
     */
    protected _getValueFromInputComponent(): FileWithLink[] | undefined {
        return this._value;
    }

    /** Update list of files */
    private _onNewFileUpdateList(): void {
        if (this._value == null) {
            this._value = [];
        }

        for (const file of this.filePicker.getFiles()) {

            const fileWithLink: FileWithLink = {
                file: file
            };

            if (!this.options.enableSameNameFile && this._isFileAlreadyInFileList(fileWithLink)) {
                continue;
            }

            if (this.options.onNewFileAdded) {
                this.options.onNewFileAdded(fileWithLink).then(res => {
                    fileWithLink.linkToDownload = res?.linkToDownload;
                    this._createListItemForFile(fileWithLink);
                }).catch(showErrorInDialog);
            } else {
                this._createListItemForFile(fileWithLink);
            }
            this._value.push(fileWithLink);
        }
        // dispatch onchange event
        this._dispatchValueChangedEvent();
    }


    /** Create an HTML <li> element linked with the file */
    private _createListItemForFile(fileWithLink: FileWithLink): void {

        const deleteBtn = new CjsButton({
            cssStyle: ButtonStyle.DANGER,
            label: this.i18n.cancel({ fileToCancel: fileWithLink.file.name }),
            icon: {
                name: "fa-xmark"
            },
            iconMode: "only",
            size: ButtonSize.SMALL,
            disabled: this.disabled
        });
        deleteBtn.clickEvent.on(_ => this._onRemoveItemBtnClick(listItem, fileWithLink));

        const listItem = createHtmlElement("li", {
            cssClass: ["list-group-item", FLEX_CONTAINER_ROW, JUSTIFY_CONTENT.BETWEEN, ALIGN_ITEMS.CENTER],
            innerHTML: [
                createHtmlElement("div", {
                    cssClass: [TEXT_TRUNCATE],
                    innerHTML: fileWithLink.linkToDownload ? "<a href=\"" + fileWithLink.linkToDownload + "\" download=\"" + fileWithLink.file.name + "\">" + fileWithLink.file.name + "</a>" : fileWithLink.file.name
                }),
                deleteBtn
            ]
        });

        listItem.append(deleteBtn);
        this.components.fileList.append(listItem);
    }


    /** Remove file from this._value and remove it's HTML element */
    private _onRemoveItemBtnClick(listItem: HTMLLIElement, fileWithLink: FileWithLink): void {

        const deletedPromise = this.options.onFileDeleted?.(fileWithLink) ?? Promise.resolve();
        deletedPromise.then(() => {

            const _value = this._getValueArray();
            const index = _value.indexOf(fileWithLink);
            if (index > -1) {

                // Remove HTML element
                this.components.fileList.removeChild(listItem);

                // Remove file from this._value
                _value.splice(index, 1);
            }
            // dispatch onchange event
            this._dispatchValueChangedEvent();
        }).catch(showErrorInDialog);

    }

    /** Check if there is a file in this._value with the same name, if so return true */
    private _isFileAlreadyInFileList(newFileWithLink: FileWithLink): boolean {
        return this._getValueArray().some(fileWithLink => fileWithLink.file.name === newFileWithLink.file.name);
    }

    /** Get file picker as input for this editor */
    public getInput(): CjsFilePicker {
        return this.filePicker;
    }

    /** Get all files name as string, join with "," */
    public convertValueToInputValue(value: FileWithLink[] | undefined): string {
        return this._getValueArray().map(fileWithLink => fileWithLink.file.name).join(", ");
    }

    /**
     * On filePicker update event is trigger
     */
    protected override _onInputValueChange(event?: anyReallyAny): void {
        this._onNewFileUpdateList();
    }

    /** Update disabled attribute on input component */
    protected _refreshDisabled(): void {
        this.filePickerOptions.setAndRefreshIfChanged({ disabled: this.disabled });
    }

}
